/**
 * DOCUMENTATION:
 * Component: Html5PracticalProject
 * Purpose: A capstone educational page demonstrating how to synthesize HTML5 concepts into a real-world project.
 * * * Note for Junior Developers:
 * - Learning tags in isolation is easy; combining them into a functional architecture is where real engineering begins.
 * - This project demonstrates an "Accessible Event Registration System".
 * - Notice how we don't just write HTML; we write HTML that plans ahead for CSS styling and JavaScript interaction.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function Html5PracticalProject() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Project Requirements Checklist
    const projectRequirements = useMemo(() => [
        { feature: "Semantic Foundation", description: "Use <header>, <main>, <section>, and <footer> to create a logical document outline." },
        { feature: "Accessible Forms", description: "Implement an event registration form using native validation (required, type='email') and properly linked <label> tags." },
        { feature: "Multimedia Integration", description: "Embed a promotional <video> with an English <track> for subtitles." },
        { feature: "Offline Resilience", description: "Use Web Storage (localStorage) to save the user's form draft in case they accidentally refresh the page." },
    ], []);

    // Coding examples demonstrating the synthesized project
    const codingModules = useMemo(() => [
        {
            title: "Phase 1: The Semantic Shell & Multimedia",
            code: `<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n  <title>Tech Conference 2026 Registration</title>\n</head>\n<body>\n  <header>\n    <h1>Annual Tech Conference</h1>\n    <nav aria-label="Main navigation">\n      <ul>\n        <li><a href="#about">About</a></li>\n        <li><a href="#register">Register</a></li>\n      </ul>\n    </nav>\n  </header>\n\n  <main>\n    \n    <section id="about">\n      <h2>Event Overview</h2>\n      <video width="100%" controls poster="event-poster.jpg">\n        <source src="promo.mp4" type="video/mp4">\n        <track kind="captions" src="promo-en.vtt" srclang="en" label="English" default>\n        Your browser does not support the video tag.\n      </video>\n    </section>\n    \n    \n  </main>\n\n  <footer>\n    <p>&copy; 2026 Tech Conference. All rights reserved.</p>\n  </footer>\n</body>\n</html>`,
            desc: "This sets up our SEO-friendly metadata, creates a clear navigation structure using <nav>, and embeds an accessible promotional video using native HTML5 tags."
        },
        {
            title: "Phase 2: The Accessible Registration Form",
            code: `<section id="register">\n  <h2>Secure Your Spot</h2>\n  <form id="registrationForm" action="/api/register" method="POST">\n    <fieldset>\n      <legend>Personal Information</legend>\n      \n      \n      <label for="fullName">Full Name:</label>\n      <input type="text" id="fullName" name="fullName" required aria-required="true">\n      \n      \n      <label for="emailAddr">Work Email:</label>\n      <input type="email" id="emailAddr" name="emailAddr" required>\n      \n      \n      <label>\n        <input type="radio" name="ticketType" value="general" checked> General Admission\n      </label>\n      <label>\n        <input type="radio" name="ticketType" value="vip"> VIP Pass\n      </label>\n\n    </fieldset>\n    \n    <button type="submit">Complete Registration</button>\n  </form>\n</section>`,
            desc: "We use <fieldset> to group related data, ensuring screen readers announce the context. Native 'required' and 'type=\"email\"' attributes enforce client-side validation instantly."
        },
        {
            title: "Phase 3: Adding 'Memory' with Web Storage API",
            code: `<script>\n  // Select elements\n  const form = document.getElementById('registrationForm');\n  const emailInput = document.getElementById('emailAddr');\n\n  // 1. On page load, check if we saved an email draft previously\n  document.addEventListener('DOMContentLoaded', () => {\n    const savedEmail = localStorage.getItem('draftEmail');\n    if (savedEmail) {\n      emailInput.value = savedEmail;\n    }\n  });\n\n  // 2. Save the email to localStorage whenever the user types\n  emailInput.addEventListener('input', (event) => {\n    localStorage.setItem('draftEmail', event.target.value);\n  });\n\n  // 3. Clear the storage only upon successful form submission\n  form.addEventListener('submit', () => {\n    // Assuming submission is successful\n    localStorage.removeItem('draftEmail');\n  });\n</script>`,
            desc: "This small JavaScript snippet dramatically improves User Experience (UX). If the user accidentally closes the tab halfway through typing their email, it will still be there when they return!"
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    PRACTICAL PROJECT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Putting It All Together
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A strong web application isn't built with just one technology; it's the harmonious orchestration of several. In this practical exercise, we are building an "Event Registration Page." This project requires us to structure our document logically for SEO, capture user data securely, embed rich media, and use the browser's memory to create a resilient user experience.
                        </Typography>

                        {/* ✅ Project Requirements Checklist */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Project Scope & Requirements</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Feature</div>
                                    <div>Implementation Strategy</div>
                                </ResponsiveDataRow>
                            )}
                            {projectRequirements.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.feature}</Box>
                                    <Box sx={{ opacity: 0.9, fontSize: "0.9rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/html5-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
